import * as yaml from 'js-yaml';

export interface YamlInput {
  rawValue: string;
  isValid: boolean;
  parsedObject: object | null;
  formattedString: string | null;
  error: string | null;
}

export class YamlFormatter {
  /**
   * Convert a JavaScript object to YAML string
   */
  static jsonToYaml(obj: object, indent: number = 2): string {
    try {
      const yamlString = yaml.dump(obj, {
        indent: indent,
        lineWidth: -1, // No line width limit for better readability
        noRefs: true, // Avoid using anchors and references
        sortKeys: false // Preserve object key order
      });
      
      return yamlString;
    } catch (error) {
      throw new Error(`Failed to convert to YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Format a YAML string with proper indentation
   */
  static formatYaml(yamlString: string, indent: number = 2): string {
    try {
      // Parse and re-dump to ensure consistent formatting
      const parsed = yaml.load(yamlString);
      if (typeof parsed === 'object' && parsed !== null) {
        return this.jsonToYaml(parsed as object, indent);
      }
      return yamlString;
    } catch (error) {
      throw new Error(`Failed to format YAML: ${error instanceof Error ? error.message : 'Invalid YAML syntax'}`);
    }
  }

  /**
   * Validate YAML syntax without parsing to object
   */
  static validateYaml(yamlString: string): boolean {
    try {
      yaml.load(yamlString);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get available YAML formatting options
   */
  static getFormattingOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 2, label: '2 spaces' },
      { value: 4, label: '4 spaces' },
      { value: 8, label: '8 spaces' }
    ];
  }

  /**
   * Create a YamlInput object from JSON string
   */
  static fromJson(jsonString: string): YamlInput {
    try {
      const parsedObject = JSON.parse(jsonString);
      const formattedString = this.jsonToYaml(parsedObject);
      
      return {
        rawValue: jsonString,
        isValid: true,
        parsedObject,
        formattedString,
        error: null
      };
    } catch (error) {
      return {
        rawValue: jsonString,
        isValid: false,
        parsedObject: null,
        formattedString: null,
        error: error instanceof Error ? error.message : 'Invalid JSON format'
      };
    }
  }

  /**
   * Create a YamlInput object from YAML string
   */
  static fromYaml(yamlString: string): YamlInput {
    try {
      const parsedObject = yaml.load(yamlString);
      
      if (typeof parsedObject !== 'object' || parsedObject === null) {
        throw new Error('YAML must parse to an object');
      }
      
      const formattedJson = JSON.stringify(parsedObject, null, 2);
      
      return {
        rawValue: yamlString,
        isValid: true,
        parsedObject,
        formattedString: formattedJson,
        error: null
      };
    } catch (error) {
      return {
        rawValue: yamlString,
        isValid: false,
        parsedObject: null,
        formattedString: null,
        error: error instanceof Error ? error.message : 'Invalid YAML format'
      };
    }
  }

  /**
   * Convert YAML string to JSON string
   */
  static yamlToJson(yamlString: string): string {
    try {
      const parsedObject = yaml.load(yamlString);
      
      if (typeof parsedObject !== 'object' || parsedObject === null) {
        throw new Error('YAML must parse to an object');
      }
      
      return JSON.stringify(parsedObject, null, 2);
    } catch (error) {
      throw new Error(`Failed to convert YAML to JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Minify YAML (remove unnecessary whitespace)
   */
  static minifyYaml(yamlString: string): string {
    try {
      const parsedObject = yaml.load(yamlString);
      if (typeof parsedObject !== 'object' || parsedObject === null) {
        throw new Error('Cannot minify invalid YAML');
      }
      
      return yaml.dump(parsedObject, {
        indent: 0,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false,
        noCompatMode: true
      });
    } catch (error) {
      throw new Error(`Failed to minify YAML: ${error instanceof Error ? error.message : 'Invalid YAML'}`);
    }
  }

  /**
   * Check if two YAML strings are semantically equivalent
   */
  static areEquivalent(yaml1: string, yaml2: string): boolean {
    try {
      const obj1 = yaml.load(yaml1);
      const obj2 = yaml.load(yaml2);
      
      return JSON.stringify(obj1) === JSON.stringify(obj2);
    } catch {
      return false;
    }
  }

  /**
   * Get common YAML preset examples
   */
  static getPresetExamples(): Array<{ name: string; json: string; yaml: string; description: string }> {
    return [
      {
        name: 'Simple Object',
        json: `{
  "name": "John Doe",
  "age": 30,
  "city": "New York"
}`,
        yaml: `name: John Doe
age: 30
city: New York`,
        description: 'Basic key-value pair structure'
      },
      {
        name: 'Nested Object',
        json: `{
  "user": {
    "name": "John",
    "profile": {
      "email": "john@example.com",
      "settings": {
        "theme": "dark",
        "notifications": true
      }
    }
  }
}`,
        yaml: `user:
  name: John
  profile:
    email: john@example.com
    settings:
      theme: dark
      notifications: true`,
        description: 'Hierarchical data structure'
      },
      {
        name: 'Array',
        json: `{
  "fruits": ["apple", "banana", "cherry"],
  "numbers": [1, 2, 3]
}`,
        yaml: `fruits:
  - apple
  - banana
  - cherry
numbers:
  - 1
  - 2
  - 3`,
        description: 'List and array examples'
      },
      {
        name: 'Mixed Types',
        json: `{
  "name": "Example App",
  "version": "1.0.0",
  "active": true,
  "config": {
    "debug": false,
    "timeout": 5000
  },
  "tags": ["production", "api"],
  "metadata": {
    "created": "2023-01-01",
    "updated": "2023-12-31"
  }
}`,
        yaml: `name: Example App
version: 1.0.0
active: true
config:
  debug: false
  timeout: 5000
tags:
  - production
  - api
metadata:
  created: '2023-01-01'
  updated: '2023-12-31'`,
        description: 'Various data types including strings, numbers, booleans'
      }
    ];
  }
}